import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowRight, ArrowUp, ChevronDown, ChevronRight, ChevronUp, Paperclip, Replace, Search, Upload, X } from "lucide-react";
import styles from "./HelpCenterPage.module.css";
import { useLanguage } from "../LanguageContext";
import { HELP_ARTICLES, HELP_CATEGORIES, POPULAR_ARTICLES, slugify } from "./helpCenterArticles";
import { TICKET_CATEGORIES } from "../useSupportTickets";

const LANG_LOCALE = { EN: "en-US", ES: "es-ES", PT: "pt-BR", FR: "fr-FR", AR: "ar-EG" };
const MAX_DETAILS = 1000;
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const ISSUE_TITLE_MAX = 40;

// Title, category and body copy, lowercased, for the article search
const articleSearchText = (article) =>
  [article.title, article.category, ...article.body.flatMap((b) => (typeof b === "string" ? b : b.list ?? [b.callout ?? b.text, b.term]))]
    .join(" ")
    .replace(/\*\*/g, "")
    .toLowerCase();

const categoryPath = (category) => `/help/${slugify(category)}`;
const articlePath = (article) => `${categoryPath(article.category)}/${article.slug}`;

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Recent tickets read as "2 hours ago" / "Yesterday"; older ones as a calendar date
function formatSubmitted(ts, now, locale) {
  const diff = now - ts;
  if (diff < 2 * DAY) {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (diff < HOUR) return capitalize(rtf.format(-Math.max(1, Math.round(diff / 60000)), "minute"));
    if (diff < DAY) return capitalize(rtf.format(-Math.round(diff / HOUR), "hour"));
    return capitalize(rtf.format(-1, "day"));
  }
  return new Intl.DateTimeFormat(locale, { month: "long", day: "2-digit", year: "numeric" }).format(ts);
}

function formatFullDate(ts, locale, month) {
  const date = new Intl.DateTimeFormat(locale, { month, day: "2-digit", year: "numeric" }).format(ts);
  const time = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", timeZoneName: "short" }).format(ts);
  return `${date} at ${time}`;
}

const STATUS_STYLES = {
  sent: { className: styles.statusSent, labelKey: "help_status_sent" },
  in_progress: { className: styles.statusInProgress, labelKey: "help_status_in_progress" },
  resolved: { className: styles.statusResolved, labelKey: "help_status_resolved" },
};

function StatusBadge({ status }) {
  const { t } = useLanguage();
  const { className, labelKey } = STATUS_STYLES[status];
  return <span className={`${styles.statusBadge} ${className}`}>{t(labelKey)}</span>;
}

// Tickets are listed by a short title (the API takes it from the description's first line)
function issueTitle(subject) {
  return subject.length > ISSUE_TITLE_MAX ? `${subject.slice(0, ISSUE_TITLE_MAX - 1).trimEnd()}…` : subject;
}

function useEscape(onClose) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
}

function Modal({ titleId, title, onClose, children }) {
  useEscape(onClose);
  return createPortal(
    <div className={styles.modalOverlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className={styles.modalHeader}>
          <h2 id={titleId} className={styles.modalTitle}>{title}</h2>
          <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close">
            <X size={22} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function CountedTextarea({ value, onChange, placeholder, className = "", ...props }) {
  return (
    <div className={styles.textareaWrap}>
      <textarea
        className={`${styles.textarea} ${className}`}
        placeholder={placeholder}
        value={value}
        maxLength={MAX_DETAILS}
        onChange={(e) => onChange(e.target.value)}
        {...props}
      />
      <span className={styles.charCount}>{value.length}/{MAX_DETAILS}</span>
    </div>
  );
}

// Textarea + screenshot picker + submit for adding information to a ticket
function DetailsComposer({ value, onChange, file, onFile, placeholder, submitLabel, canSubmit, onSubmit }) {
  const { t } = useLanguage();
  const fileRef = useRef(null);
  return (
    <>
      <CountedTextarea value={value} onChange={onChange} placeholder={placeholder} />
      {file && (
        <p className={styles.fileChip}>
          <Paperclip size={14} />
          <span>{file.name}</span>
          <button type="button" onClick={() => onFile(null)} aria-label="Remove screenshot">
            <X size={14} />
          </button>
        </p>
      )}
      <div className={styles.composerActions}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            onFile(e.target.files[0] ?? null);
            e.target.value = "";
          }}
        />
        <button type="button" className={styles.secondaryBtn} onClick={() => fileRef.current.click()}>
          <Replace size={20} />
          {t("help_upload_screenshot")}
        </button>
        <button type="button" className={styles.primaryBtn} disabled={!canSubmit} onClick={onSubmit}>
          {submitLabel}
        </button>
      </div>
    </>
  );
}

function Timeline({ items }) {
  return (
    <ul className={styles.timeline}>
      {items.map((item, i) => (
        <li key={i} className={styles.timelineItem}>
          <span className={styles.timelineText}>
            {item.url ? (
              <a href={item.url} target="_blank" rel="noreferrer" className={styles.timelineLink}>{item.text}</a>
            ) : (
              item.text
            )}
            {item.attachment && <Paperclip size={18} className={styles.clipIcon} />}
          </span>
          <span className={styles.timelineMeta}>{item.meta}</span>
        </li>
      ))}
    </ul>
  );
}

// Ticket ID / submitted / status / category rows shared by the details and confirmation modals
function TicketSummaryRows({ ticket, shortDate = false }) {
  const { t, langCode } = useLanguage();
  const locale = LANG_LOCALE[langCode] ?? "en-US";
  return (
    <>
      <div className={styles.detailRow}>
        <dt>{t("help_ticket_id")}</dt>
        <dd>#{ticket.id}</dd>
      </div>
      <div className={styles.detailRow}>
        <dt>{t("help_submitted")}</dt>
        <dd>
          {!shortDate && <span className={styles.dateLong}>{formatFullDate(ticket.submittedAt, locale, "long")}</span>}
          <span className={shortDate ? undefined : styles.dateShort}>{formatFullDate(ticket.submittedAt, locale, "short")}</span>
        </dd>
      </div>
      <div className={styles.detailRow}>
        <dt>{t("help_status")}</dt>
        <dd><StatusBadge status={ticket.status} /></dd>
      </div>
      <div className={styles.detailRow}>
        <dt>{t("help_category")}</dt>
        <dd>{ticket.category}</dd>
      </div>
    </>
  );
}

// The conversation's messages as description lines (the trader's own, then the team's
// replies) and every screenshot as attachment lines
function threadItems(thread, t) {
  const messages = [];
  const attachments = [];
  let traderMessages = 0;
  let traderFiles = 0;
  for (const message of thread) {
    if (message.kind !== "message") continue;
    const fromTeam = message.role === "Team";
    if (message.body) {
      messages.push({
        text: message.body,
        meta: fromTeam ? t("help_team_reply") : traderMessages === 0 ? t("help_original_message") : t("help_additional_detail"),
      });
    }
    if (!fromTeam) traderMessages += 1;
    for (const file of message.attachments) {
      attachments.push({
        text: file.name,
        url: file.url,
        attachment: true,
        meta: fromTeam ? t("help_team_reply") : traderFiles === 0 ? t("help_original_attachment") : t("help_additional_attachment"),
      });
      if (!fromTeam) traderFiles += 1;
    }
  }
  return { messages, attachments };
}

// Loads the full ticket (with its conversation) from the API when it opens
function TicketDetailsModal({ summary, onClose, loadTicket, onAddUpdate }) {
  const { t } = useLanguage();
  const [ticket, setTicket] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [details, setDetails] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    loadTicket(summary.id).then((result) => {
      if (!active) return;
      if (result.error) setLoadError(result.error);
      else setTicket(result.ticket);
    });
    return () => {
      active = false;
    };
  }, [summary.id, loadTicket]);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    const result = await onAddUpdate(summary.id, { text: details.trim(), file });
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setTicket(result.ticket);
    setDetails("");
    setFile(null);
  }

  const shown = ticket ?? summary;
  const { messages, attachments } = ticket ? threadItems(ticket.thread, t) : { messages: [], attachments: [] };

  return (
    <Modal titleId="ticket-details-title" title={t("help_ticket_details")} onClose={onClose}>
      <dl className={styles.detailList}>
        <TicketSummaryRows ticket={shown} />
        <div className={`${styles.detailRow} ${styles.detailRowTop}`}>
          <dt>{t("help_description")}</dt>
          <dd>
            {ticket ? (
              <Timeline items={messages} />
            ) : (
              <span className={loadError ? styles.errorText : undefined}>{loadError ?? t("help_loading")}</span>
            )}
          </dd>
        </div>
        {attachments.length > 0 && (
          <div className={`${styles.detailRow} ${styles.detailRowTop}`}>
            <dt>{t("help_attachment")}</dt>
            <dd>
              <Timeline items={attachments} />
            </dd>
          </div>
        )}
      </dl>

      <div className={styles.composer}>
        <p className={styles.composerLabel}>{t("help_need_more_info")}</p>
        <DetailsComposer
          value={details}
          onChange={setDetails}
          file={file}
          onFile={setFile}
          placeholder={t("help_add_details_placeholder")}
          submitLabel={submitting ? t("help_sending") : t("help_add_update")}
          canSubmit={!submitting && Boolean(details.trim() || file)}
          onSubmit={handleSubmit}
        />
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
    </Modal>
  );
}

function CategoryDropdown({ value, onChange }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className={`${styles.select} ${open ? styles.selectOpen : ""}`} ref={ref}>
      <button
        type="button"
        id="report-category"
        className={`${styles.selectTrigger} ${value ? "" : styles.selectPlaceholder}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{value ?? t("help_select_category")}</span>
        {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {open && (
        <ul className={styles.selectMenu} role="listbox" aria-labelledby="report-category">
          {TICKET_CATEGORIES.map((category) => (
            <li
              key={category}
              role="option"
              aria-selected={category === value}
              className={`${styles.selectOption} ${category === value ? styles.selectOptionActive : ""}`}
              onMouseDown={() => {
                onChange(category);
                setOpen(false);
              }}
            >
              {category}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Dropzone({ file, onFile }) {
  const { t } = useLanguage();
  const [dragging, setDragging] = useState(false);

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped?.type.startsWith("image/")) onFile(dropped);
  }

  return (
    <label
      className={`${styles.dropzone} ${dragging ? styles.dropzoneActive : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <input
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          onFile(e.target.files[0] ?? null);
          e.target.value = "";
        }}
      />
      {file ? <Paperclip size={26} className={styles.dropzoneIcon} /> : <Upload size={26} className={styles.dropzoneIcon} />}
      <span>{file ? file.name : t("help_upload_or_drag")}</span>
    </label>
  );
}

// Form → "Issue reported" confirmation; the new ticket is added on submit
function ReportIssueModal({ initialCategory = null, onClose, onSubmit }) {
  const { t } = useLanguage();
  const [category, setCategory] = useState(initialCategory);
  const [details, setDetails] = useState("");
  const [file, setFile] = useState(null);
  const [reported, setReported] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function reset() {
    setCategory(initialCategory);
    setDetails("");
    setFile(null);
    setReported(null);
    setError(null);
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    const result = await onSubmit({ category, details: details.trim(), file });
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    // The confirmation shows what was sent, screenshot name included
    setReported({ ...result.ticket, attachments: file ? [file.name] : [] });
  }

  if (reported) {
    return (
      <Modal titleId="report-issue-title" title={t("help_issue_reported")} onClose={onClose}>
        <p className={styles.successNote}>{t("help_issue_reported_note")}</p>
        <dl className={`${styles.detailList} ${styles.detailListPlain}`}>
          <TicketSummaryRows ticket={reported} shortDate />
          <div className={styles.detailRow}>
            <dt>{t("help_description")}</dt>
            <dd>{issueTitle(reported.issue)}</dd>
          </div>
          {reported.attachments.length > 0 && (
            <div className={styles.detailRow}>
              <dt>{t("help_attachment")}</dt>
              <dd className={styles.attachmentValue}>
                {reported.attachments[0]}
                <Paperclip size={18} className={styles.clipIcon} />
              </dd>
            </div>
          )}
        </dl>
        <div className={`${styles.modalActions} ${styles.modalActionsSuccess}`}>
          <button type="button" className={styles.secondaryBtn} onClick={reset}>
            {t("help_report_another")}
          </button>
          <button type="button" className={styles.outlineBtn} onClick={onClose}>
            {t("help_done")}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal titleId="report-issue-title" title={t("help_report_issue_title")} onClose={onClose}>
      <div className={styles.reportForm}>
        <div className={styles.field}>
          <label className={styles.composerLabel} htmlFor="report-category">{t("help_category")}</label>
          <CategoryDropdown value={category} onChange={setCategory} />
        </div>
        <div className={styles.field}>
          <label className={styles.composerLabel} htmlFor="report-details">{t("help_issue_description")}</label>
          <p className={styles.fieldHint}>{t("help_issue_description_hint")}</p>
          <CountedTextarea
            id="report-details"
            className={styles.textareaTall}
            value={details}
            onChange={setDetails}
            placeholder={t("help_describe_placeholder")}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.composerLabel}>{t("help_attachment_optional")}</span>
          <p className={styles.fieldHint}>{t("help_attachment_hint")}</p>
          <Dropzone file={file} onFile={setFile} />
        </div>
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
      <div className={`${styles.modalActions} ${styles.modalActionsReport}`}>
        <button type="button" className={styles.secondaryBtn} onClick={onClose}>
          {t("help_cancel")}
        </button>
        <button
          type="button"
          className={styles.primaryBtn}
          disabled={submitting || !category || !details.trim()}
          onClick={handleSubmit}
        >
          {submitting ? t("help_sending") : t("help_submit_issue")}
        </button>
      </div>
    </Modal>
  );
}

function ArticleGrid({ articles, onNavigate }) {
  return (
    <div className={styles.articleGrid}>
      {articles.map((article) => (
        <a
          key={articlePath(article)}
          className={styles.articleCard}
          href={articlePath(article)}
          onClick={(e) => {
            e.preventDefault();
            onNavigate(articlePath(article));
          }}
        >
          <span>{article.title}</span>
          <ArrowRight size={18} className={styles.articleArrow} />
        </a>
      ))}
    </div>
  );
}

// `items` is [{ label, path? }]; the last crumb is the current page
function Breadcrumbs({ items, onNavigate }) {
  return (
    <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
      {items.map((item, i) => (
        <span key={item.label} className={styles.crumb}>
          {i > 0 && <ChevronRight size={16} className={styles.crumbSeparator} aria-hidden="true" />}
          {item.path ? (
            <a
              href={item.path}
              className={styles.crumbLink}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(item.path);
              }}
            >
              {item.label}
            </a>
          ) : (
            <span className={styles.crumbCurrent} aria-current="page">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

// Renders **bold** spans inside article copy
function RichText({ text }) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}

function ArticleBlock({ block }) {
  if (typeof block === "string") return <p><RichText text={block} /></p>;
  if (block.list) {
    return (
      <ul className={styles.articleBullets}>
        {block.list.map((item) => <li key={item}><RichText text={item} /></li>)}
      </ul>
    );
  }
  if (block.callout) return <p className={styles.articleCallout}><RichText text={block.callout} /></p>;
  return (
    <div>
      <p className={styles.articleTerm}>{block.term}</p>
      <p><RichText text={block.text} /></p>
    </div>
  );
}

function ArticleView({ article, onNavigate }) {
  const { t } = useLanguage();
  return (
    <>
      <Breadcrumbs
        onNavigate={onNavigate}
        items={[
          { label: t("help_title"), path: "/help" },
          { label: article.category, path: categoryPath(article.category) },
          { label: article.title },
        ]}
      />
      <h1 className={`${styles.title} ${styles.articleTitle}`}>{article.title}</h1>
      <div className={styles.articleBody}>
        {article.body.map((block, i) => <ArticleBlock key={i} block={block} />)}
      </div>
    </>
  );
}

function SectionView({ category, onNavigate, onReport }) {
  const { t } = useLanguage();
  return (
    <>
      <Breadcrumbs onNavigate={onNavigate} items={[{ label: t("help_title"), path: "/help" }, { label: category }]} />
      <header className={`${styles.hero} ${styles.sectionHero}`}>
        <h1 className={styles.title}>{category}</h1>
        <button type="button" className={`${styles.primaryBtn} ${styles.reportBtn}`} onClick={onReport}>
          {t("help_report_category_issue").replace("{category}", category)}
        </button>
      </header>
      <section className={`${styles.section} ${styles.sectionArticles}`}>
        <h2 className={styles.sectionTitle}>{t("help_articles_title")}</h2>
        <ArticleGrid articles={HELP_ARTICLES.filter((a) => a.category === category)} onNavigate={onNavigate} />
      </section>
    </>
  );
}

// `subPath` is what follows /help: "" (overview), "/portfolio" (section) or "/portfolio/<article>"
// `support` is App's useSupportTickets(), so the list survives leaving the help center. `pendingReport` is a
// category to open the report modal with on arrival (e.g. "KYC" from Portfolio's "Contact support");
// `onReportOpened` clears it.
export default function HelpCenterPage({ subPath = "", onNavigate, support, pendingReport, onReportOpened }) {
  const { tickets, status: ticketsStatus, reload: reloadTickets, createTicket, loadTicket, addUpdate } = support;
  const { t, langCode } = useLanguage();
  const locale = LANG_LOCALE[langCode] ?? "en-US";
  const [now] = useState(() => Date.now());
  const [tab, setTab] = useState("open");
  const [sortDesc, setSortDesc] = useState(true);
  const [query, setQuery] = useState("");
  const [openTicketId, setOpenTicketId] = useState(null);
  // undefined = closed; null or a category name = open (pre-selected on section pages)
  const [reportCategory, setReportCategory] = useState(undefined);

  useEffect(() => {
    if (pendingReport === undefined) return;
    setReportCategory(pendingReport);
    onReportOpened();
  }, [pendingReport, onReportOpened]);

  const [categorySlug, articleSlug] = subPath.split("/").filter(Boolean);
  const category = HELP_CATEGORIES.find((c) => slugify(c) === categorySlug);
  const article = category && articleSlug
    ? HELP_ARTICLES.find((a) => a.category === category && a.slug === articleSlug)
    : undefined;
  const isUnknownPath = Boolean(categorySlug) && (!category || (articleSlug && !article));

  useEffect(() => {
    if (isUnknownPath) onNavigate("/help");
  }, [isUnknownPath, onNavigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [subPath]);

  const openTickets = tickets.filter((tk) => tk.status !== "resolved");
  const resolvedTickets = tickets.filter((tk) => tk.status === "resolved");
  const visibleTickets = [...(tab === "open" ? openTickets : resolvedTickets)].sort((a, b) =>
    sortDesc ? b.submittedAt - a.submittedAt : a.submittedAt - b.submittedAt,
  );
  const openTicket = tickets.find((tk) => tk.id === openTicketId);

  const trimmedQuery = query.trim().toLowerCase();
  const articles = useMemo(() => {
    if (!trimmedQuery) return POPULAR_ARTICLES;
    return HELP_ARTICLES.filter((a) => articleSearchText(a).includes(trimmedQuery));
  }, [trimmedQuery]);

  async function handleReport(report) {
    const result = await createTicket(report);
    if (result.ticket) {
      setTab("open");
      setQuery("");
    }
    return result;
  }

  const modals = (
    <>
      {openTicket && (
        <TicketDetailsModal
          summary={openTicket}
          onClose={() => setOpenTicketId(null)}
          loadTicket={loadTicket}
          onAddUpdate={addUpdate}
        />
      )}
      {reportCategory !== undefined && (
        <ReportIssueModal
          initialCategory={reportCategory}
          onClose={() => setReportCategory(undefined)}
          onSubmit={handleReport}
        />
      )}
    </>
  );

  if (isUnknownPath) return null;

  if (category) {
    return (
      <section className={styles.page}>
        {article ? (
          <ArticleView article={article} onNavigate={onNavigate} />
        ) : (
          <SectionView category={category} onNavigate={onNavigate} onReport={() => setReportCategory(category)} />
        )}
        {modals}
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <header className={styles.hero}>
        <div>
          <h1 className={styles.title}>{t("help_title")}</h1>
          <p className={styles.subtitle}>{t("help_subtitle")}</p>
        </div>
        <button type="button" className={`${styles.primaryBtn} ${styles.reportBtn}`} onClick={() => setReportCategory(null)}>
          {t("help_report_issue")}
        </button>
      </header>

      <label className={styles.searchWrap}>
        <Search size={22} className={styles.searchIcon} />
        <input
          type="search"
          placeholder={t("help_search_placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>

      {!trimmedQuery && (
        <>
          <section className={styles.section}>
            <div className={styles.sectionTop}>
              <h2 className={styles.sectionTitle}>{t("help_support_tickets")}</h2>
              <div className={styles.tabs} role="tablist">
                {[
                  { key: "open", label: t("help_tab_open"), count: openTickets.length },
                  { key: "resolved", label: t("help_tab_resolved"), count: resolvedTickets.length },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === item.key}
                    className={`${styles.tab} ${tab === item.key ? styles.tabActive : ""}`}
                    onClick={() => setTab(item.key)}
                  >
                    {item.label} ({item.count})
                  </button>
                ))}
              </div>
            </div>

            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("help_issue")}</th>
                  <th className={styles.hideMobile}>{t("help_category")}</th>
                  <th className={styles.hideMobile}>
                    <button
                      type="button"
                      className={styles.sortBtn}
                      onClick={() => setSortDesc((d) => !d)}
                      aria-label={sortDesc ? "Sort oldest first" : "Sort newest first"}
                    >
                      {t("help_submitted")}
                      {sortDesc ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
                    </button>
                  </th>
                  <th>{t("help_status")}</th>
                  <th className={`${styles.hideMobile} ${styles.actionCol}`}>{t("help_action")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleTickets.map((tk) => (
                  <tr key={tk.id} className={styles.ticketRow} onClick={() => setOpenTicketId(tk.id)}>
                    <td className={styles.issueCell}>{issueTitle(tk.issue)}</td>
                    <td className={styles.hideMobile}>{tk.category}</td>
                    <td className={styles.hideMobile}>{formatSubmitted(tk.submittedAt, now, locale)}</td>
                    <td><StatusBadge status={tk.status} /></td>
                    <td className={`${styles.hideMobile} ${styles.actionCol}`}>
                      <button
                        type="button"
                        className={styles.linkBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTicketId(tk.id);
                        }}
                      >
                        {tk.status === "resolved" ? t("help_manage") : t("help_see_details")}
                      </button>
                    </td>
                  </tr>
                ))}
                {visibleTickets.length === 0 && (
                  <tr>
                    <td colSpan={5} className={styles.emptyCell}>
                      {ticketsStatus === "loading" ? (
                        t("help_loading")
                      ) : ticketsStatus === "error" ? (
                        <>
                          {t("help_tickets_error")}{" "}
                          <button type="button" className={styles.linkBtn} onClick={reloadTickets}>
                            {t("help_try_again")}
                          </button>
                        </>
                      ) : (
                        t("help_no_tickets")
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t("help_faq")}</h2>
            <div className={styles.faqGrid}>
              {HELP_CATEGORIES.map((category) => (
                <a
                  key={category}
                  href={categoryPath(category)}
                  className={styles.faqCard}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate(categoryPath(category));
                  }}
                >
                  <span className={styles.faqTitle}>{category}</span>
                  <span className={styles.faqCount}>
                    {HELP_ARTICLES.filter((a) => a.category === category).length} {t("help_articles")}
                  </span>
                </a>
              ))}
            </div>
          </section>
        </>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t("help_popular_articles")}</h2>
        {articles.length > 0 ? (
          <ArticleGrid articles={articles} onNavigate={onNavigate} />
        ) : (
          <p className={styles.noResults}>{t("help_no_results")}</p>
        )}
      </section>

      {modals}
    </section>
  );
}
